'use client';
import { useEffect, useRef, useState } from 'react';
import { useT } from '../lib/i18n';
import { useLayerLock } from '../lib/useLayerLock';
import { Icon } from './Icon';

/**
 * 계정 화면 공용 조각 — 가이드(login/register/find-account) 마크업 규약을 그대로 쓴다.
 * 클래스는 가이드 이름 그대로(form-field·form-input·form-pw-wrap·form-input-group …).
 */

/** 비밀번호 칸 — 눈 토글(가이드 form-pw-toggle) */
export function PwInput({ value, onChange, placeholder, autoComplete, onEnter }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  onEnter?: () => void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="form-pw-wrap">
      <input type={shown ? 'text' : 'password'} className="form-input" value={value} placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }} />
      {/* tabIndex={-1} — 탭은 입력칸끼리만 옮겨 다녀야 한다(2026-08-22 사용자 지적).
          눈 토글은 마우스·스페이스로 쓰는 보조 버튼이라 순서에서 뺀다 */}
      <button type="button" className="form-pw-toggle" tabIndex={-1} onClick={() => setShown(!shown)}
        aria-label={shown ? 'hide' : 'show'}>
        {/*
          아이콘은 **지금 상태**를 말한다 — 가려져 있으면 빗금 눈, 보이면 눈.
          (2026-08-27: 이 자리를 af922e6 로 고쳤다가 1fc6fb4 로 되돌렸고, 다시 같은 지적을
          받아 확정한다. 기준은 "누르면 할 일" 이 아니라 "지금 어떤 상태인가" 다.)
        */}
        <Icon name={shown ? 'eye' : 'eye-off'} />
      </button>
    </div>
  );
}

/** 필수 표시가 붙는 라벨 */
export function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="form-label">
      {children}{required && <span className="form-required"> *</span>}
    </label>
  );
}

/** 오류·안내 문구 한 줄 */
/**
 * 입력에서 걸러낸 글자를 **말해 준다**(2026-09-02 사용자 제보 — 아이디 칸에 한글이 안 들어간다).
 *
 * 규칙에 없는 글자는 애초에 안 들어가게 막고 있는데(한글을 IME 로 넣고 통과라 믿는 일을 막는다),
 * **조용히 사라져서** 쓰는 사람에게는 "키보드가 안 먹는다" 로 읽혔다. 무엇이 안 되는지 그 자리에서 말한다.
 *
 * 토스트가 아니라 **칸 아래 한 줄**이다 — 한글은 글자마다 조합이 확정돼 토스트면 연달아 뜨고,
 * 시선은 입력칸에 있는데 토스트는 화면 아래에 뜬다.
 *
 * ```tsx
 * const drop = useInputFilter();
 * <input value={f.uid} onChange={(e) => set({ uid: drop.filter('uid', e.target.value, /[^a-zA-Z0-9]/g, 'acc.onlyAlnum') })} />
 * <FieldMsg error={errors.uid} hint={drop.hint('uid') ?? okHint} />
 * ```
 */
export function useInputFilter() {
  const t = useT();
  const [dropped, setDropped] = useState<Record<string, string | null>>({});
  return {
    /** 규칙 밖 글자를 걷어 내고, 걷어 냈으면 안내 문구를 켠다(다음에 제대로 치면 꺼진다) */
    filter(field: string, raw: string, deny: RegExp, msgKey: string): string {
      const next = raw.replace(deny, '');
      setDropped((s) => ({ ...s, [field]: next === raw ? null : msgKey }));
      return next;
    },
    /** 그 칸의 안내 문구 — 없으면 null */
    hint(field: string): string | null {
      const key = dropped[field];
      return key ? t(key) : null;
    },
  };
}

export function FieldMsg({ error, hint }: { error?: string | null; hint?: string | null }) {
  if (error) return <p className="form-hint" style={{ color: '#dc2626' }}>{error}</p>;
  if (hint) return <p className="form-hint">{hint}</p>;
  return null;
}

/**
 * 인증코드 칸 — 발송·재발송·3분 타이머(가이드 fa-timer).
 * 코드는 **서버가 메일/문자로** 보내고, 여기는 입력과 남은 시간만 다룬다.
 */
export function SendCodeButton({ onSend, sending, disabled, label }: {
  onSend: () => void; sending?: boolean; disabled?: boolean; label?: string;
}) {
  const t = useT();
  return (
    <button type="button" className="action-btn action-submit btn-sm form-btn-nowrap"
      disabled={sending || disabled} onClick={onSend}>
      {label ?? t('acc.sendCode')}
    </button>
  );
}

/**
 * 인증코드 입력 — **인증을 받는 칸 바로 아래**에 붙인다(2026-08-22 사용자 지적:
 * 이메일 인증이면 이메일 아래, 알림톡 인증이면 전화번호 아래). 그래서 발송 버튼과
 * 분리했다 — 버튼은 각 칸의 입력 그룹 안에(SendCodeButton), 이 칸은 그 아래 형제로.
 */
export function AuthCodeField({ label, code, onChange, seconds, onSend, sending, error }: {
  label: string;
  code: string;
  onChange: (v: string) => void;
  seconds: number;
  onSend: () => void;
  sending?: boolean;
  error?: string | null;
}) {
  const t = useT();
  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <div className="form-field">
      <Label required>{label}</Label>
      <div className="form-input-group">
        <input type="text" className="form-input" maxLength={6} inputMode="numeric"
          value={code} placeholder={t('acc.codePh')} onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} />
        <span className="fa-timer">{seconds > 0 ? mmss : t('acc.codeExpired')}</span>
        <button type="button" className="action-btn action-cancel btn-sm form-btn-nowrap"
          disabled={sending} onClick={onSend}>{t('acc.resend')}</button>
      </div>
      <FieldMsg error={error} />
    </div>
  );
}

/** 3분 카운트다운 — 발송할 때마다 다시 시작한다 */
export function useCountdown(seconds = 180) {
  const [left, setLeft] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const start = () => {
    setLeft(seconds);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => setLeft((v) => (v <= 1 ? 0 : v - 1)), 1000);
  };
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);
  return { left, start };
}

/**
 * 우편번호 검색 — 마켓 설정(주소 자동완성)에 따라 **입력 방식이 달라진다**(2026-08-22 사용자).
 * 켜져 있으면 **화면 안 레이어**로 Daum 우편번호 서비스를 띄워 고르고, 꺼져 있으면 직접 입력한다.
 */
export function ZipField({ zipcode, address, addressDetail, onChange, autoAddress }: {
  zipcode: string;
  address: string;
  addressDetail: string;
  onChange: (v: { zipcode?: string; address?: string; addressDetail?: string }) => void;
  autoAddress: boolean;
}) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  /**
   * 검색을 못 쓰게 됐을 때 직접 입력으로 풀어 준다(2026-08-22).
   * 자동완성이 켜진 마켓은 칸을 읽기 전용으로 두는데, 스크립트가 막히거나(사내망·차단)
   * 실패하면 **아무것도 입력할 수 없어 가입이 멈춘다**. 그때는 잠금을 푼다.
   */
  const [fallback, setFallback] = useState(false);
  const locked = autoAddress && !fallback;
  /** 레이어 열림 여부 — 새 창(popup)이 아니라 화면 안 레이어로 띄운다(2026-08-22 사용자 요청) */
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 스크롤 잠금 + Esc 닫기. **딤 클릭으로는 닫지 않는다**(주소를 고르는 중 잘못 눌리면 처음부터 다시다)
  useLayerLock(open, () => setOpen(false));

  /**
   * 우편번호 찾기 — **화면 안 레이어**(가이드 layer-popup 규약)에 끼워 넣는다.
   * 새 창은 팝업 차단에 걸리고 모바일에서 특히 불편하다 — Daum 서비스의 `embed(요소)` 를 쓴다.
   * 스크립트는 처음 누를 때만 싣는다(안 쓰는 방문자에게 외부 스크립트를 강요하지 않는다).
   */
  const openFinder = async () => {
    if (open) return; // 이미 열려 있다 — 다시 끼우면 화면이 깜빡인다
    setLoading(true);
    try {
      const w = window as unknown as {
        daum?: { Postcode: new (o: object) => { embed: (el: HTMLElement, o?: object) => void } };
      };
      if (!w.daum?.Postcode) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('postcode script'));
          document.head.appendChild(s);
        });
      }
      setOpen(true);
      // 레이어가 그려진 뒤에 끼운다 — 빈 요소에 embed 하면 높이가 0 이 된다
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      const host = boxRef.current;
      if (!host) return;
      host.innerHTML = '';
      new w.daum!.Postcode({
        oncomplete: (data: { zonecode: string; roadAddress: string; jibunAddress: string }) => {
          onChange({ zipcode: data.zonecode, address: data.roadAddress || data.jibunAddress });
          setOpen(false);
        },
        onclose: () => setOpen(false), // 서비스 안 닫기 버튼으로 닫았을 때
        // 상자를 그대로 채운다 — 높이는 CSS(.zip-finder-box)가 정하므로 여기서 숫자를 박지 않는다
        width: '100%',
        height: '100%',
      }).embed(host, { autoClose: true });
    } catch {
      setFallback(true); // 검색을 못 쓴다 — 직접 입력으로 푼다
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="form-field">
        <Label required>{t('acc.zipcode')}</Label>
        <div className="form-input-group">
          {/* 잠긴 칸을 눌러도 검색이 열린다(2026-08-22 사용자 요청) — 직접 못 쓰는 칸을 눌렀을 때
              아무 일도 안 나면 막힌 것처럼 보인다. 잠겼을 때만 손가락 커서로 눌러도 됨을 알린다.
              초점(onFocus)으로는 열지 않는다 — 레이어를 닫으면 초점이 칸으로 돌아와 다시 열린다 */}
          <input type="text" className="form-input form-input-zip" value={zipcode} placeholder={t('acc.zipcode')}
            readOnly={locked} style={locked ? { cursor: 'pointer' } : undefined}
            onClick={locked ? () => void openFinder() : undefined}
            onChange={(e) => onChange({ zipcode: e.target.value.replace(/\D/g, '') })} />
          {autoAddress && (
            <button type="button" className="action-btn action-cancel btn-sm form-btn-nowrap"
              disabled={loading} onClick={() => void openFinder()}>
              <Icon name="search" size={14} /> {t('acc.findAddress')}
            </button>
          )}
        </div>
        {fallback && <p className="form-hint">{t('acc.addressManual')}</p>}
      </div>

      {/* 우편번호 찾기 레이어 — 가이드 layer-popup 규약. 닫기는 X 버튼과 Esc */}
      {open && (
        <>
          {/* 딤 클릭으로는 닫지 않는다 — 주소를 고르는 중 잘못 눌리면 처음부터 다시다 */}
          <div className="layer-backdrop open"></div>
          <div className="layer-popup open zip-finder">
            <div className="layer-header">
              <h2 className="layer-title">{t('acc.findAddress')}</h2>
              <button type="button" className="layer-close" onClick={() => setOpen(false)} aria-label={t('notice.close')}>
                <Icon name="x" />
              </button>
            </div>
            {/* 서비스가 이 요소 안에 자기 화면을 그린다 — 높이는 CSS(.zip-finder-box)가 정한다 */}
            <div className="layer-body zip-finder-body"><div ref={boxRef} className="zip-finder-box" /></div>
          </div>
        </>
      )}
      <div className="form-field">
        <Label required>{t('acc.address')}</Label>
        <input type="text" className="form-input mb6" value={address} placeholder={t('acc.address')}
          readOnly={locked} style={locked ? { cursor: 'pointer' } : undefined}
          onClick={locked ? () => void openFinder() : undefined}
          onChange={(e) => onChange({ address: e.target.value })} />
        {/* 상세주소는 검색으로 채울 수 없다 — 자동완성이 켜져 있어도 늘 직접 입력이다 */}
        <input type="text" className="form-input" value={addressDetail} placeholder={t('acc.addressDetail')}
          onChange={(e) => onChange({ addressDetail: e.target.value })} />
      </div>
    </>
  );
}

/** 소셜 로그인 버튼 — 마켓에서 켠 것만(가이드 login-sns-btn). 연동은 온보딩 몫이라 자리만 만든다 */
export function SocialButtons({ social, mode }: {
  social: { google: boolean; apple: boolean; kakao: boolean; naver: boolean };
  mode: 'login' | 'join';
}) {
  const t = useT();
  const list = (['google', 'apple', 'kakao', 'naver'] as const).filter((k) => social[k]);
  if (!list.length) return null;
  return (
    <div className="login-sns-btns">
      {list.map((k) => (
        <a key={k} className={`login-sns-btn ${k}`} href={`/api/auth/social/${k}?mode=${mode}`}>
          <span className="login-sns-icon"><SocialIcon provider={k} /></span>
          {t(`acc.social.${k}`)}
        </a>
      ))}
    </div>
  );
}

/** 소셜 아이콘 — 가이드의 인라인 SVG 를 그대로 옮겼다(lucide 에 브랜드 로고가 없다) */
function SocialIcon({ provider }: { provider: 'google' | 'apple' | 'kakao' | 'naver' }) {
  if (provider === 'google') return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
  if (provider === 'apple') return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.42.07 2.41.74 3.23.8 1.23-.24 2.41-.95 3.72-.86 1.59.13 2.79.76 3.57 1.94-3.28 1.96-2.5 5.96.48 7.17-.62 1.67-1.42 3.3-3 3.83zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
  if (provider === 'kakao') return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden>
      <path d="M12 3C6.48 3 2 6.48 2 11c0 2.83 1.66 5.29 4.14 6.8L5.2 21l4.14-2.17c.87.18 1.75.27 2.66.27 5.52 0 10-3.48 10-8S17.52 3 12 3z" />
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden>
      <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z" />
    </svg>
  );
}
